import { ChangeDetectionStrategy, Component, output, signal } from '@angular/core';

import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { DateRangeTranslationMapper, IRangeSelectOption, DateRange } from '@dss-libs/shared/type/common-types';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'dss-libs-ui-date-range-dropdown',
  imports: [MatMenu, TranslocoDatePipe, TranslocoDirective, MatMenuTrigger],
  templateUrl: './date-range-dropdown.component.html',
  styleUrl: './date-range-dropdown.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslocoDatePipe],
})
export class DateRangeDropdownComponent {
  readonly dateRangeSelected = output<IRangeSelectOption>();
  readonly #today = new Date();
  readonly #lastYearStart = new Date(this.#today.getFullYear() - 1, 0, 1);
  readonly #lastYearEnd = new Date(this.#today.getFullYear() - 1, 11, 31);
  readonly #lastMonthStart = new Date(this.#today.getFullYear(), this.#today.getMonth() - 1, 1);
  readonly #lastMonthEnd = new Date(
    this.#today.getFullYear(),
    this.#today.getMonth(),
    0 // 0th day of current month = last day of previous month
  );
  readonly #currentMonthStart = new Date(this.#today.getFullYear(), this.#today.getMonth(), 1);
  readonly #currentMonthEnd = new Date(this.#today.getFullYear(), this.#today.getMonth() + 1, 0);
  readonly #lastWeek = new Date(this.#today.getFullYear(), this.#today.getMonth(), this.#today.getDate() - 7);

  readonly dateRanges = signal<IRangeSelectOption[]>([
    {
      label: DateRangeTranslationMapper.get(DateRange.LastWeek)!,
      rangeStart: this.#lastWeek,
      rangeEnd: this.#today,
      value: DateRange.LastWeek,
    },
    {
      label: DateRangeTranslationMapper.get(DateRange.LastMonth)!,
      rangeStart: this.#lastMonthStart,
      rangeEnd: this.#lastMonthEnd,
      value: DateRange.LastMonth,
    },
    {
      label: DateRangeTranslationMapper.get(DateRange.CurrentMonth)!,
      rangeStart: this.#currentMonthStart,
      rangeEnd: this.#currentMonthEnd,
      value: DateRange.CurrentMonth,
    },
    {
      label: DateRangeTranslationMapper.get(DateRange.LastYear)!,
      rangeStart: this.#lastYearStart,
      rangeEnd: this.#lastYearEnd,
      value: DateRange.LastYear,
    },
    {
      label: DateRangeTranslationMapper.get(DateRange.AllTime)!,
      value: DateRange.AllTime,
    },
  ]).asReadonly();

  readonly selectedDateRange = signal(this.dateRanges()[4]);

  selectDateRange(option: IRangeSelectOption) {
    this.selectedDateRange.set(option);
    this.dateRangeSelected.emit(option);
  }

  protected readonly DateRange = DateRange;
}
